import type { ReactNode } from 'react';
import React from 'react';

import type { BasketCTAButtonProps } from 'src/components/molecules/BasketCTAButton/BasketCTAButton';
import { BasketCTAButton } from 'src/components/molecules/BasketCTAButton/BasketCTAButton';
import type { PriceBreakdownStateProps } from 'src/components/molecules/PriceBreakdown/PriceBreakdown';
import { PriceBreakdown } from 'src/components/molecules/PriceBreakdown/PriceBreakdown';
import type { QuantitySelectorProps } from 'src/components/molecules/QuantitySelector/QuantitySelector';
import { QuantitySelector } from 'src/components/molecules/QuantitySelector/QuantitySelector';
import * as styles from './ProductOptionsDrawerFooter.styles';

export const DRAWER_ID_SUFFIX = 'drawer';

export const DRAWER_CTA_ROW_TESTID = 'drawer-cta-row';

export interface ProductOptionsDrawerFooterProps {
  basketCta: BasketCTAButtonProps;
  /**
   * A slot rather than props: the real wallets are a client-only lazy container that paints its
   * buttons through the Adyen SDK, so stories stand in for them here.
   */
  expressCheckout: ReactNode;
  /** Null when there is no total to show, which is a product with no options or one out of stock. */
  priceBreakdown: PriceBreakdownStateProps | null;
  quantitySelector: QuantitySelectorProps;
}

/**
 * The drawer's sticky footer: what the product costs, how many of it, and the ways to buy it.
 *
 * Pure, so every state it has can be seen in Storybook without a store. The drawer above it holds
 * the hooks that fill these props.
 *
 * The quantity input is here as well as on the page, so a shopper part-way through personalising
 * does not have to close the drawer to change how many they want. How that row shares its width is
 * ctaRow's decision, in ProductOptionsDrawerFooter.styles.
 */
export const ProductOptionsDrawerFooter = ({
  basketCta,
  expressCheckout,
  priceBreakdown,
  quantitySelector,
}: ProductOptionsDrawerFooterProps) => (
  <div css={styles.footer}>
    {priceBreakdown && <PriceBreakdown {...priceBreakdown} />}
    <QuantitySelector {...quantitySelector} idSuffix={DRAWER_ID_SUFFIX} />
    <div css={styles.ctaRow} data-testid={DRAWER_CTA_ROW_TESTID}>
      <div css={styles.ctaSlot}>
        <BasketCTAButton {...basketCta} isDrawerCta />
      </div>
      {expressCheckout}
    </div>
  </div>
);
